<template>
  <div class="register col-xl-4 mx-auto">
    <h3 class="mb-4">Update your account</h3>
    <b-form-group label="Name" :state="IsFieldValid($v.user.name)" :invalid-feedback="RequiredAndMaxLengthMessage('Name', $v.user.name)">
      <b-form-input v-model="$v.user.name.$model" :state="IsFieldValid($v.user.name)" />
    </b-form-group>
    <b-form-group label="Phone" :state="IsFieldValid($v.user.phone)" :invalid-feedback="RequiredAndPhoneMessage($v.user.phone)">
      <b-form-input v-model="$v.user.phone.$model" :state="IsFieldValid($v.user.phone)" />
    </b-form-group>
    <b-form-group label="Company Name" :state="IsFieldValid($v.user.companyName)" :invalid-feedback="RequiredAndMaxLengthMessage('Company Name', $v.user.companyName)">
      <b-form-input v-model="$v.user.companyName.$model" :state="IsFieldValid($v.user.companyName)" />
    </b-form-group>
    <b-form-group label="Title" :state="IsFieldValid($v.user.title)" :invalid-feedback="RequiredAndMaxLengthMessage('Title', $v.user.title)">
      <b-form-input v-model="$v.user.title.$model" :state="IsFieldValid($v.user.title)" />
    </b-form-group>
    <b-button @click="update" variant="success" :disabled="isSaving">
      <span v-if="!isSaving">Update</span>
      <span v-else><b-spinner small></b-spinner> Please Wait...</span>
    </b-button>
    <div class="extras mt-2">
      <span class="account-item text-right">
        <a href="#" @click="changeEmailClicked" class="account-link">Change email address</a>
      </span>
      <span class="text-center center-pipe">|</span>
      <span class="account-item text-left">
        <a href="#" @click="changePasswordClicked" class="account-link">Change password</a>
      </span>
    </div>
    <b-alert show :variant="userSaved ? 'info' : 'danger'" v-if="userSaved || error" class="text-left mt-3">
      <p class="mb-0">{{ userSaved ? 'User information saved successfully.' : error }}</p>
    </b-alert>

    <b-modal ref="email-modal" title="Change your email address" header-text-variant="dark" body-text-variant="dark" centered @hidden="emailModalHidden" @show="emailModalShow">
      <template slot="default">
        <b-form-group label="Current email">
          <b-form-input :value="user.email" disabled />
        </b-form-group>
        <b-form-group label="New email" :state="IsFieldValid($v.newEmail)" :invalid-feedback="EmailMessage('New Email', $v.newEmail)">
          <b-form-input v-model="$v.newEmail.$model" :state="IsFieldValid($v.newEmail)" />
        </b-form-group>
        <b-form-group label="Current password" :state="IsFieldValid($v.currentPassword)" :invalid-feedback="RequiredMessage('Password', $v.currentPassword)">
          <b-form-input v-model="$v.currentPassword.$model" :state="IsFieldValid($v.currentPassword)" type="password" />
        </b-form-group>
        <b-alert show :variant="emailSaved ? 'info' : 'danger'" v-if="emailSaved || emailError" class="text-left">
          <p class="mb-0">{{ emailSaved ? 'Email updated successfully.' : emailError }}</p>
        </b-alert>
      </template>
      <template slot="modal-footer" class="float-right">
        <b-button variant="danger" @click="closeEmailClicked">Close</b-button>
        <b-button variant="success" @click="updateEmailClicked" class="ml-1" :disabled="isSaving">
          <span v-if="!isSaving">Update</span>
          <span v-else><b-spinner small></b-spinner> Please Wait...</span>
        </b-button>
      </template>
    </b-modal>

    <b-modal ref="password-modal" title="Change your password" header-text-variant="dark" body-text-variant="dark" centered @hidden="passwordModalHidden" @show="passwordModalShow">
      <template slot="default">
        <b-form-group label="New Password" :state="IsFieldValid($v.newPassword)" :invalid-feedback="PasswordMessage('New Password', $v.newPassword)">
          <b-form-input v-model="$v.newPassword.$model" :state="IsFieldValid($v.newPassword)" type="password" />
        </b-form-group>
        <b-form-group label="Confirm New Password" :state="IsFieldValid($v.confirmPassword)" invalid-feedback="Passwords must match.">
          <b-form-input v-model="$v.confirmPassword.$model" :state="IsFieldValid($v.confirmPassword)" type="password" />
        </b-form-group>
        <b-form-group label="Current Password" :state="IsFieldValid($v.currentPassword)" :invalid-feedback="RequiredMessage('Password', $v.currentPassword)">
          <b-form-input v-model="$v.currentPassword.$model" :state="IsFieldValid($v.currentPassword)" type="password" />
        </b-form-group>
        <b-alert show :variant="passwordSaved ? 'info' : 'danger'" v-if="passwordSaved || passwordError" class="text-left">
          <p class="mb-0">{{ passwordSaved ? 'Password updated successfully.' : passwordError }}</p>
        </b-alert>
      </template>
      <template slot="modal-footer" class="float-right">
        <b-button variant="danger" @click="closePasswordClicked">Close</b-button>
        <b-button variant="success" @click="updatePasswordClicked" class="ml-1" :disabled="isSaving">
          <span v-if="!isSaving">Update</span>
          <span v-else><b-spinner small></b-spinner> Please Wait...</span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import UserService from '../services/UserService.js'
import { required, maxLength, minLength, sameAs, email } from 'vuelidate/lib/validators'
import CustomValidations from '../mixins/customValidations.js'
const phoneValidation = CustomValidations.phoneValidation
const pwValidation = CustomValidations.passwordValidation

export default {
  name: 'AccountUpdate',
  props: {
    userProp: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      user: this.userProp,
      newEmail: '',
      newPassword: '',
      currentPassword: '',
      confirmPassword: '',
      error: '',
      userSaved: false,
      emailError: '',
      emailSaved: false,
      passwordSaved: false,
      passwordError: '',
      isSaving: false
    }
  },
  validations() {
    return {
      newEmail: {
        required,
        email,
        maxLength: maxLength(256)
      },
      newPassword: {
        required,
        pwValidation,
        minLength: minLength(15),
        maxLength: maxLength(60)
      },
      currentPassword: {
        required
      },
      confirmPassword: {
        sameAsPassword: sameAs(function() {
          return this.newPassword
        })
      },
      user: {
        name: {
          required,
          maxLength: maxLength(50)
        },
        phone: {
          required,
          phoneValidation
        },
        companyName: {
          required,
          maxLength: maxLength(100)
        },
        title: {
          required,
          maxLength: maxLength(50)
        }
      }
    }
  },
  methods: {
    changeEmailClicked() {
      this.$refs['email-modal'].show()
    },
    closeEmailClicked() {
      this.$refs['email-modal'].hide()
    },
    emailModalHidden() {
      this.currentPassword = ''
      this.newEmail = ''
      this.emailSaved = false
      this.emailError = ''
    },
    emailModalShow() {
      this.$v.currentPassword.$reset()
      this.$v.newEmail.$reset()
    },
    updateEmailClicked() {
      this.emailError = ''
      this.emailSaved = false
      this.isSaving = true
      UserService.updateEmail({ newEmail: this.newEmail, currentPassword: this.currentPassword }).then(res => {
        const userToken = res.data
        this.$store.dispatch('user/setUserData', { email: userToken.email, name: userToken.name, role: userToken.role, token: userToken.token, refreshToken: userToken.refreshToken }).then(() => {       
          this.emailSaved = true
          this.isSaving = false
          this.user.email = this.newEmail
        })
      }).catch(error => {
        this.isSaving = false
        this.emailError = error && error.response && error.response.data ? error.response.data : 'Email could not be updated.'
      })
    },
    updatePasswordClicked() {
      this.passwordError = ''
      this.passwordSaved = false
      this.isSaving = true
      UserService.updatePassword({ currentPassword: this.currentPassword, newPassword: this.newPassword }).then(res => {
        const userToken = res.data
        this.$store.dispatch('user/setUserData', { email: userToken.email, name: userToken.name, role: userToken.role, token: userToken.token, refreshToken: userToken.refreshToken }).then(() => {       
          this.passwordSaved = true
          this.isSaving = false
        })
      }).catch(error => {
        this.isSaving = false
        this.passwordError = error && error.response && error.response.data ? error.response.data : 'Password could not be updated.'
      })
    },
    changePasswordClicked() {
      this.$refs['password-modal'].show()
    },
    closePasswordClicked() {
      this.$refs['password-modal'].hide()
    },
    passwordModalHidden() {
      this.currentPassword = ''
      this.newPassword = ''
      this.confirmPassword = ''
      this.passwordSave = false
      this.passwordError = ''
    },
    passwordModalShow() {
      this.$v.currentPassword.$reset()
      this.$v.newPassword.$reset()
      this.$v.confirmPassword.$reset()
    },
    update() {
      this.userSaved = false

      const u = this.$v.user
      u.name.$touch()
      u.phone.$touch()
      u.companyName.$touch()
      u.title.$touch()

      if (u.name.$invalid || u.phone.$invalid || u.companyName.$invalid || u.title.$invalid) return

      this.isSaving = true

      UserService.updateUser(this.user).then(res => {
        const userToken = res.data
        this.$store.dispatch('user/setUserData', { email: userToken.email, name: userToken.name, role: userToken.role, token: userToken.token, refreshToken: userToken.refreshToken }).then(() => {       
          this.userSaved = true
          this.isSaving = false
        })
      }).catch(error => {
        this.isSaving = false
        this.error = error && error.response && error.response.data ? error.response.data : 'The account could not be updated.'
      })
    }
  }
}
</script>

<style scoped>
.extras .account-item {
  display: inline-block;
  width: 48%;
}

.center-pipe {
  width: 4%;
  margin-left: 6px;
  margin-right: 6px;
}
</style>